import React from 'react';
import Header from '../components/Header';
import { Lock, Eye, Shield, Database, Globe, UserCheck, Trash2 } from 'lucide-react';
import { Seo } from '../src/seo/Seo';

const Privacy: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      <Seo
        title={{ fr: 'Politique de confidentialité — Burok', en: 'Privacy policy — Burok' }}
        description={{
          fr: 'Découvrez comment Burok collecte, utilise et protège vos données personnelles, ainsi que vos droits RGPD.',
          en: 'Learn how Burok collects, uses, and protects your personal data, and understand your privacy rights.',
        }}
        canonicalPath={{ fr: '/privacy', en: '/en/privacy' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-indigo-100 rounded-full">
            <Lock className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Politique de Confidentialité
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="p-8 bg-white rounded-2xl shadow-xl md:p-12">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Shield className="mr-3 w-6 h-6 text-green-500" />
                Notre Engagement
              </h2>
              <p className="leading-relaxed text-gray-700">
                Chez Burok, nous respectons votre vie privée et nous nous engageons à protéger vos données personnelles. 
                Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
              </p>
            </div>

            {/* Data Collection */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Database className="mr-3 w-6 h-6 text-blue-500" />
                Données Collectées
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-6 bg-blue-50 rounded-xl">
                  <h3 className="flex items-center mb-3 font-semibold text-blue-900">
                    <UserCheck className="mr-2 w-5 h-5" />
                    Informations de compte
                  </h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li>• Nom et prénom</li>
                    <li>• Adresse e-mail</li>
                    <li>• Numéro de téléphone (optionnel)</li>
                    <li>• Date de naissance</li>
                  </ul>
                </div>
                <div className="p-6 bg-purple-50 rounded-xl">
                  <h3 className="flex items-center mb-3 font-semibold text-purple-900">
                    <Globe className="mr-2 w-5 h-5" />
                    Données d'utilisation
                  </h3>
                  <ul className="space-y-2 text-sm text-purple-800">
                    <li>• Historique des transactions</li>
                    <li>• Préférences de budget</li>
                    <li>• Données de navigation</li>
                    <li>• Informations techniques</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Use Data */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Eye className="mr-3 w-6 h-6 text-indigo-500" />
                Utilisation des Données
              </h2>
              <p className="mb-6 leading-relaxed text-gray-700">
                Nous utilisons vos données uniquement pour les finalités suivantes :
              </p>
              <div className="space-y-4">
                <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-2 mr-4 w-3 h-3 bg-indigo-500 rounded-full"></div>
                  <div>
                    <h4 className="mb-1 font-semibold text-gray-900">Fournir nos services</h4>
                    <p className="text-sm text-gray-600">Gestion de vos finances, suivi des dépenses et budgétisation</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-2 mr-4 w-3 h-3 bg-indigo-500 rounded-full"></div>
                  <div>
                    <h4 className="mb-1 font-semibold text-gray-900">Améliorer l'expérience</h4>
                    <p className="text-sm text-gray-600">Optimisation des fonctionnalités et personnalisation</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-2 mr-4 w-3 h-3 bg-indigo-500 rounded-full"></div>
                  <div>
                    <h4 className="mb-1 font-semibold text-gray-900">Support client</h4>
                    <p className="text-sm text-gray-600">Assistance technique et résolution des problèmes</p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-2 mr-4 w-3 h-3 bg-indigo-500 rounded-full"></div>
                  <div>
                    <h4 className="mb-1 font-semibold text-gray-900">Sécurité</h4>
                    <p className="text-sm text-gray-600">Protection contre la fraude et sécurisation des comptes</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Sharing */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Partage des Données
              </h2>
              <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                <h3 className="mb-3 font-semibold text-yellow-900">Nous ne vendons JAMAIS vos données</h3>
                <p className="mb-4 text-yellow-800">
                  Vos données personnelles ne sont jamais vendues, louées ou partagées avec des tiers à des fins commerciales.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <p className="text-sm text-yellow-800">Partage uniquement avec votre consentement explicite</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <p className="text-sm text-yellow-800">Fournisseurs de services techniques (hébergement, sécurité)</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <p className="text-sm text-yellow-800">Obligations légales ou réglementaires</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Shield className="mr-3 w-6 h-6 text-green-500" />
                Sécurité des Données
              </h2>
              <p className="mb-6 leading-relaxed text-gray-700">
                Nous mettons en œuvre des mesures de sécurité robustes pour protéger vos données :
              </p>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="mb-3 font-semibold text-green-900">Protection technique</h3>
                  <ul className="space-y-2 text-sm text-green-800">
                    <li>• Chiffrement AES-256</li>
                    <li>• Connexions HTTPS sécurisées</li>
                    <li>• Authentification multi-facteurs</li>
                    <li>• Surveillance 24/7</li>
                  </ul>
                </div>
                <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="mb-3 font-semibold text-green-900">Protection organisationnelle</h3>
                  <ul className="space-y-2 text-sm text-green-800">
                    <li>• Accès restreint aux données</li>
                    <li>• Formation du personnel</li>
                    <li>• Audits de sécurité réguliers</li>
                    <li>• Procédures de gestion des incidents</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* User Rights */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Vos Droits RGPD
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="mb-2 font-semibold text-indigo-900">Droit d'accès</h4>
                  <p className="text-sm text-indigo-800">Consulter toutes vos données personnelles</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="mb-2 font-semibold text-indigo-900">Droit de rectification</h4>
                  <p className="text-sm text-indigo-800">Corriger des informations inexactes</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="mb-2 font-semibold text-indigo-900">Droit à l'effacement</h4>
                  <p className="text-sm text-indigo-800">Supprimer vos données (droit à l'oubli)</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="mb-2 font-semibold text-indigo-900">Droit à la portabilité</h4>
                  <p className="text-sm text-indigo-800">Récupérer vos données dans un format standard</p>
                </div>
              </div>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Conservation des Données
              </h2>
              <p className="mb-4 leading-relaxed text-gray-700">
                Nous conservons vos données uniquement le temps nécessaire à la réalisation des finalités pour lesquelles 
                elles ont été collectées :
              </p>
              <div className="p-6 bg-gray-50 rounded-xl">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-gray-500 rounded-full"></div>
                    <span><strong>Données de compte :</strong> Tant que votre compte est actif + 3 ans après désactivation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-gray-500 rounded-full"></div>
                    <span><strong>Données financières :</strong> 7 ans (obligation légale comptable)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 mt-2 mr-3 w-2 h-2 bg-gray-500 rounded-full"></div>
                    <span><strong>Données techniques :</strong> 2 ans maximum</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact and Complaints */}
            <div className="p-8 text-center bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Questions ou Réclamations ?
              </h2>
              <p className="mb-6 text-gray-600">
                Pour exercer vos droits ou signaler une préoccupation, contactez notre délégué à la protection des données.
              </p>
              <div className="space-y-3">
                <a
                  href="mailto:privacy@burok.app"
                  className="inline-flex items-center px-6 py-3 mr-3 font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105"
                >
                  <Lock className="mr-2 w-4 h-4" />
                  Contact DPO
                </a>
                <a
                  href="/data/delete"
                  className="inline-flex items-center px-6 py-3 font-medium text-white bg-red-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105"
                >
                  <Trash2 className="mr-2 w-4 h-4" />
                  Supprimer mes données
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
